import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { vacancyQueryKeys } from "./queryKeys";

import { instance } from "@/lib/axios/instance";
import { VacancyFilters } from "@/lib/contexts/Vacancy/types";
import { GetVacanciesResponse } from "@/types/response/GetVacanciesResponse";

type UseVacanciesParams = {
  city?: string[];
  clientId?: string;
  country?: string[];
  department?: string[];
  descending?: boolean | null;
  field?: string[];
  orderBy?: string | null;
  page?: number;
  pageSize?: number;
  search?: string;
  includeUnpublished?: boolean;
  channels?: string[];
  publicationChannels?: string[];
  publicationIds?: string[];
  company?: string[];
  district?: string[];
  segmentId?: string;
} & VacancyFilters;

/**
 * Hook to fetch a paginated list of vacancies with filtering, sorting, and search capabilities.
 *
 * @param params - Query parameters for filtering and pagination
 * @returns React Query result containing vacancies data, loading state, and error state
 *
 * @example
 * ```tsx
 * const { data: vacancies, isLoading } = useVacancies({
 *   page: 1,
 *   pageSize: 10,
 *   search: "developer",
 *   clientId: "123"
 * });
 * ```
 */
export function useVacancies(params: UseVacanciesParams) {
  return useQuery<GetVacanciesResponse, AxiosError>({
    queryKey: vacancyQueryKeys.list({
      city: params.city,
      clientId: params.clientId,
      country: params.country,
      department: params.department,
      descending: params.descending,
      field: params.field,
      orderBy: params.orderBy,
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      includeUnpublished: params.includeUnpublished,
      channels: params.channels,
      publicationChannels: params.publicationChannels,
      publicationIds: params.publicationIds,
      company: params.company,
      district: params.district,
      segmentId: params.segmentId,
    }),
    queryFn: () =>
      instance
        .get(`/api/v2/Vacancies`, {
          params: {
            city: params.city,
            clientId: params.clientId,
            country: params.country,
            department: params.department,
            descending: params.descending,
            field: params.field,
            orderBy: params.orderBy,
            page: params.page,
            pageSize: params.pageSize,
            search: params.search,
            includeUnpublished: params.includeUnpublished,
            channels: params.channels,
            publicationChannels: params.publicationChannels,
            publicationIds: params.publicationIds,
            company: params.company,
            district: params.district,
            segmentId: params.segmentId,
          },
        })
        .then((res) => res.data),
    placeholderData: keepPreviousData,
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
  });
}
